import { useState, useCallback, useRef } from 'react';
import { openContextMenu } from '../../store/eventContextMenuStore';
import { openDateSelectionMenu } from '../../store/dateSelectionMenuStore';
import {
  buildEventCreationFormUrl,
  buildFastEditPayload,
  getEventDuplicateUrl,
  getEventOpenFormModalUrl,
  getEventRecurrence,
} from './fullCalendarUseCase';

// ============================================================================
// CONSTANTS
// ============================================================================

export const DAYS = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

export const ONE_DAY_DURATION = { days: 1 };
export const ONE_WEEK_DURATION = { weeks: 1 };
export const ONE_MONTH_DURATION = { months: 1 };
export const ONE_YEAR_DURATION = { years: 1 };

export const CALENDAR_VIEWS = {
  // Horizontal views (timeline)
  customResourceTimelineDay: {
    type: 'resourceTimelineDay',
    duration: ONE_DAY_DURATION,
    buttonText: 'Jour',
  },
  customResourceTimelineWeek: {
    type: 'resourceTimeline',
    duration: ONE_WEEK_DURATION,
    buttonText: 'Semaine',
  },
  customResourceTimelineMonth: {
    type: 'resourceTimeline',
    duration: ONE_MONTH_DURATION,
    buttonText: 'Mois',
  },
  customResourceTimelineYear: {
    type: 'resourceTimeline',
    duration: ONE_YEAR_DURATION,
    buttonText: 'Année',
  },
  // Vertical views (timeGrid)
  customTimeGridDay: {
    type: 'timeGridDay',
    buttonText: 'Jour',
  },
  customTimeGridWeek: {
    type: 'timeGridWeek',
    buttonText: 'Semaine',
  },
  customResourceTimeGridDay: {
    type: 'resourceTimeGridDay',
    buttonText: 'R-Jour',
  },
  customResourceTimeGridWeek: {
    type: 'resourceTimeGridWeek',
    buttonText: 'R-Semaine',
  },
  // List views (dayGrid)
  customDayGridDay: {
    type: 'dayGrid',
    duration: { days: 1 },
    buttonText: 'Liste jour',
  },
  customDayGridWeek: {
    type: 'dayGridWeek',
    buttonText: 'Liste semaine',
  },
  customDayGridThreeMonth: {
    type: 'dayGridMonth',
    duration: { months: 3 },
    buttonText: 'Liste trimestre',
  },
  customDayGridMonth: {
    type: 'dayGridMonth',
    duration: { months: 1 },
    buttonText: 'Liste mois',
  },
  // Custom views
  threeCurrentdaysHorizontal: {
    visibleRange: (/** @type {Date} */ currentDate) => {
      const start = new Date(currentDate.valueOf());
      start.setDate(start.getDate() - 1);
      const end = new Date(currentDate.valueOf());
      end.setDate(end.getDate() + 1);
      return { start, end };
    },
    type: 'resourceTimeline',
    buttonText: '3 jours',
  },
  threeCurrentdaysList: {
    visibleRange: (/** @type {Date} */ currentDate) => {
      const start = new Date(currentDate.valueOf());
      start.setDate(start.getDate() - 1);
      const end = new Date(currentDate.valueOf());
      end.setDate(end.getDate() + 1);
      return { start, end };
    },
    type: 'dayGrid',
    buttonText: '3 jours',
  },
};

export const DEFAULT_RESOURCE_AREA_WIDTH = 150;
export const STACKED_PERIOD_RESOURCE_AREA_WIDTH = 200;
export const DEFAULT_SLOT_MIN_WIDTH = 28;
export const DAY_COLUMN_SLOT_MIN_WIDTH = 44;
export const DAY_COLUMN_SLOT_DURATION = '24:00:00';
export const DAY_COLUMN_SLOT_MIN_TIME = '00:00:00';
export const DAY_COLUMN_SLOT_MAX_TIME = '24:00:00';

/** Delay (ms) before an event becomes draggable or a date selectable on touch devices. */
export const DEFAULT_LONG_PRESS_DELAY = 350;

// ============================================================================
// UTILITY FUNCTIONS - Duration and Time Calculations
// ============================================================================

/**
 * Format duration in milliseconds to h:mm format
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string} - Formatted duration (e.g., "3:45")
 */
export const formatDuration = (durationMs) => {
  const totalMinutes = Math.floor(durationMs / 1000 / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Get the duration of an event
 * @param {import('@fullcalendar/core').EventApi} event
 * @returns {string} - The duration of the event in hours and minutes
 */
export const getEventDuration = (event) => {
  if (event.start && event.end) {
    const durationMs = event.end.getTime() - event.start.getTime();
    const minutes = Math.floor((durationMs / 1000 / 60) % 60);
    const hours = Math.floor(durationMs / 1000 / 60 / 60);
    return `${hours}h ${minutes}m`;
  }
  return 'Unknown duration';
};

// ============================================================================
// UTILITY FUNCTIONS - Resource Calculations
// ============================================================================

/**
 * Calculate resource amplitude (gap between first and last event) for a specific date range
 * @param {string} resourceId - Resource ID
 * @param {Array<CalendarEvent>} events - All events
 * @param {Date} rangeStart - Start of the date range
 * @param {Date} rangeEnd - End of the date range
 * @returns {string|null} - Formatted amplitude (h:mm) or null if less than 2 events
 */
export const calculateResourceAmplitude = (resourceId, events, rangeStart, rangeEnd) => {
  const resourceEvents = events.filter((event) => {
    const eventStart = new Date(event.start);
    const eventEnd = event.end ? new Date(event.end) : eventStart;
    const isInRange = eventStart < rangeEnd && eventEnd > rangeStart;
    const matchesResource = event.resourceId === resourceId
      || (event.resourceIds && event.resourceIds.includes(resourceId));
    const isNotBackground = event.display !== 'background';
    return matchesResource && isInRange && isNotBackground;
  }).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  if (resourceEvents.length < 2) return null;

  const firstEventStart = new Date(resourceEvents[0].start);
  const lastEvent = resourceEvents[resourceEvents.length - 1];
  const lastEventEnd = lastEvent.end ? new Date(lastEvent.end) : new Date(lastEvent.start);

  const amplitudeMs = lastEventEnd.getTime() - firstEventStart.getTime();
  return formatDuration(amplitudeMs);
};

/**
 * Calculate total duration of all events for a resource in a specific date range
 * @param {string} resourceId - Resource ID
 * @param {Array<CalendarEvent>} events - All events
 * @param {Date} rangeStart - Start of the date range
 * @param {Date} rangeEnd - End of the date range
 * @returns {string} - Formatted total duration (h:mm)
 */
export const calculateResourceDuration = (resourceId, events, rangeStart, rangeEnd) => {
  const resourceEvents = events.filter((event) => {
    const eventStart = new Date(event.start);
    const eventEnd = event.end ? new Date(event.end) : eventStart;
    const isInRange = eventStart < rangeEnd && eventEnd > rangeStart;
    const matchesResource = event.resourceId === resourceId
      || (event.resourceIds && event.resourceIds.includes(resourceId));
    const isNotBackground = event.display !== 'background';
    return matchesResource && isInRange && isNotBackground;
  });

  const totalDurationMs = resourceEvents.reduce((total, event) => {
    const start = new Date(event.start);
    const end = event.end ? new Date(event.end) : start;
    return total + (end.getTime() - start.getTime());
  }, 0);

  return formatDuration(totalDurationMs);
};

/**
 *
 * @param {CalendarScope} scope
 * @returns {{days?: number, weeks?: number, months?: number, years?: number}}
 */
export const getDuration = (scope) => {
  if (!scope) {
    return ONE_WEEK_DURATION;
  }
  switch (scope) {
    case 'day':
      return ONE_DAY_DURATION;
    case 'week':
      return ONE_WEEK_DURATION;
    case 'month':
      return ONE_MONTH_DURATION;
    case 'year':
      return ONE_YEAR_DURATION;
    default:
      return ONE_WEEK_DURATION;
  }
};

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Custom hook to manage FullCalendar state and behavior
 * @param {FullCalendarData} data
 * @returns {{
 *   handleEventClick: (arg: import('@fullcalendar/core').EventClickArg) => void,
 *   handleEventDidMount: (info: import('@fullcalendar/core').EventMountArg) => void,
 *   handleEventWillUnmount: (info: import('@fullcalendar/core').EventMountArg) => void,
 *   handleContextEdit: (event: import('@fullcalendar/core').EventApi) => void,
 *   handleDateSelection: (arg: import('@fullcalendar/core').DateSelectArg) => void,
 *   focusedEvent: import('@fullcalendar/core').EventApi | null,
 *   closeEventFocus: () => void,
 *   getResourceLabelData: (resourceId: string, viewType: string, rangeStart: Date, rangeEnd: Date) => {duration: string, amplitude: string | null}
 *   handleEventResize: (arg: import('@fullcalendar/interaction').EventResizeDoneArg) => void
 *   showRecurrenceWarningModal: boolean,
 *   handleEventDrop: (arg: import('@fullcalendar/core').EventDropArg) => void,
 *   cancelRecurrenceScope: () => void,
 *   confirmRecurrenceScope: (scope: ApiManagerRecurrenceEditionScope) => void,
 *   getDuration: (scope: CalendarScope) => {days?: number, weeks?: number, months?: number, years?: number}
 * }}
 * @param {object} [options]
 * @param {(formUrl: string) => void} [options.onOpenEventForm] - Open event form side panel
 * @param {(fastEditUrl: string, payload: ApiManagerFastEditEventCreate, eventId: string, callbacks: { onError?: () => void }) => void} [options.onFastEditEvent]
 * @param {(duplicateUrl: string) => void} [options.onDuplicateEvent]
 */
const useCalendar = (data, options = {}) => {
  const {
    onOpenEventForm,
    onFastEditEvent,
    onDuplicateEvent,
  } = options;
  // if data is undefined or null, fill it with empty objects
  const calendarData = data || {
    viewType: 'horizontalresourceDay',
    events: [],
    resources: [],
    resourcesAreaColumns: [],
    addFormUrls: [],
    viewConfig: {
      fcView: 'resourceTimeline',
      duration: { days: 1 },
      splitBy: null,
      usesSlotTimes: true,
      hourHeaders: true,
      dayColumns: false,
    },
  };

  // ============================================================================
  // STATE
  // ============================================================================

  // Events
  const [focusedEvent, setFocusedEvent] = useState(null);

  // Modals
  const [pendingFastEdit, setPendingFastEdit] = useState(/** @type {{ info: import('@fullcalendar/core').EventDropArg | import('@fullcalendar/interaction').EventResizeDoneArg } | null} */ (null));

  const showRecurrenceWarningModal = pendingFastEdit !== null;

  // ============================================================================
  // EVENT HANDLERS - Event Management
  // ============================================================================

  const commitFastEdit = useCallback((
    /** @type {import('@fullcalendar/core').EventDropArg | import('@fullcalendar/interaction').EventResizeDoneArg} */ info,
    /** @type {ApiManagerRecurrenceEditionScope} */ scope,
  ) => {
    const built = buildFastEditPayload(info, scope);
    if (!built) {
      info.revert();
      return;
    }
    if (!onFastEditEvent) {
      info.revert();
      return;
    }
    onFastEditEvent(built.fastEditUrl, built.payload, built.eventId, {
      onError: () => info.revert(),
    });
  }, [onFastEditEvent]);

  const handleFastEditInteraction = useCallback((
    /** @type {import('@fullcalendar/core').EventDropArg | import('@fullcalendar/interaction').EventResizeDoneArg} */ interactionInfo,
  ) => {
    const built = buildFastEditPayload(interactionInfo, 'none');
    if (!built) {
      interactionInfo.revert();
      return;
    }
    const recurrence = getEventRecurrence(interactionInfo.event);
    if (!recurrence) {
      commitFastEdit(interactionInfo, 'none');
      return;
    }
    setPendingFastEdit({ info: interactionInfo });
  }, [commitFastEdit]);

  const handleEventClick = useCallback((/** @type {import('@fullcalendar/core').EventClickArg} */ info) => {
    const duplicateUrl = getEventDuplicateUrl(info.event);
    if ((info.jsEvent.ctrlKey || info.jsEvent.altKey || info.jsEvent.metaKey) && duplicateUrl) {
      onDuplicateEvent?.(duplicateUrl);
      return;
    }
    setFocusedEvent(info.event);
  }, [onDuplicateEvent]);

  const handleEventResize = useCallback((/** @type {import('@fullcalendar/interaction').EventResizeDoneArg} */ eventResizeInfo) => {
    handleFastEditInteraction(eventResizeInfo);
  }, [handleFastEditInteraction]);

  const handleEventDrop = useCallback((/** @type {import('@fullcalendar/core').EventDropArg} */ eventDropInfo) => {
    handleFastEditInteraction(eventDropInfo);
  }, [handleFastEditInteraction]);

  // ============================================================================
  // EVENT HANDLERS - Context Menu (appelés par EventContextMenu avec event/calendar du store)
  // ============================================================================

  const contextMenuListenersRef = useRef(/** @type {Map<HTMLElement, (e: MouseEvent) => void>} */ (new Map()));

  const handleEventDidMount = useCallback((/** @type {import('@fullcalendar/core').EventMountArg} */ info) => {
    const listener = (/** @type {MouseEvent} */ e) => {
      e.preventDefault();
      openContextMenu(e.clientX, e.clientY, info.event, info.view.calendar);
    };
    info.el.addEventListener('contextmenu', listener);
    contextMenuListenersRef.current.set(info.el, listener);
  }, []);

  const handleEventWillUnmount = useCallback((/** @type {import('@fullcalendar/core').EventMountArg} */ info) => {
    const listener = contextMenuListenersRef.current.get(info.el);
    if (listener) {
      info.el.removeEventListener('contextmenu', listener);
      contextMenuListenersRef.current.delete(info.el);
    }
  }, []);

  const handleContextEdit = useCallback((/** @type {import('@fullcalendar/core').EventApi} */ event) => {
    const formUrl = getEventOpenFormModalUrl(event);
    if (formUrl && onOpenEventForm) {
      onOpenEventForm(formUrl);
    }
  }, [onOpenEventForm]);

  const closeEventFocus = useCallback(() => {
    setFocusedEvent(null);
  }, []);

  const handleDateSelection = useCallback((/** @type {import('@fullcalendar/core').DateSelectArg} */ selectInfo) => {
    const selection = {
      start: selectInfo.start,
      end: selectInfo.end,
      resourceId: selectInfo.resource?.id ?? null,
    };
    const urls = calendarData.addFormUrls ?? [];
    if (urls.length === 0) {
      return;
    }
    if (urls.length === 1) {
      onOpenEventForm?.(buildEventCreationFormUrl(urls[0].url, selection));
      return;
    }
    openDateSelectionMenu(selection);
  }, [calendarData.addFormUrls, onOpenEventForm]);

  // ============================================================================
  // EVENT HANDLERS - Recurrence Management
  // ============================================================================

  const cancelRecurrenceScope = useCallback(() => {
    pendingFastEdit?.info.revert();
    setPendingFastEdit(null);
  }, [pendingFastEdit]);

  const confirmRecurrenceScope = useCallback((/** @type {ApiManagerRecurrenceEditionScope} */ scope) => {
    if (!pendingFastEdit) {
      return;
    }
    commitFastEdit(pendingFastEdit.info, scope);
    setPendingFastEdit(null);
  }, [commitFastEdit, pendingFastEdit]);

  // ============================================================================
  // RESOURCE MANAGEMENT
  // ============================================================================

  const getResourceLabelData = useCallback((/** @type {string} */ resourceId, /** @type {string} */ viewType, /** @type {Date} */ rangeStart, /** @type {Date} */ rangeEnd) => {
    const isDayView = viewType.includes('Day');
    const duration = calculateResourceDuration(resourceId, calendarData.events, rangeStart, rangeEnd);
    const amplitude = isDayView
      ? calculateResourceAmplitude(resourceId, calendarData.events, rangeStart, rangeEnd)
      : null;

    return { duration, amplitude };
  }, [calendarData.events]);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Events
    focusedEvent,
    closeEventFocus,
    handleEventClick,
    handleEventResize,
    handleEventDrop,
    handleDateSelection,

    // Context menu (eventDidMount/eventWillUnmount + actions)
    handleEventDidMount,
    handleEventWillUnmount,
    handleContextEdit,

    // Recurrence
    showRecurrenceWarningModal,
    cancelRecurrenceScope,
    confirmRecurrenceScope,

    // Resources
    getResourceLabelData,

    // Views
    getDuration,
  };
};

export default useCalendar;
